import React, { useState, useEffect, useRef, useContext } from 'react';
import { collection, query, where, getDocs, getDoc, doc, updateDoc, arrayUnion, arrayRemove, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { UserContext } from '../context/UserContext';
import Layout from '../components/Layout';
import '../css/FriendsPage.css';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '@mui/material';
import { Search, PersonAdd, Check, Close, Message, Person, Send } from '@mui/icons-material';

// Custom debounce hook for search optimization
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    
    return debouncedValue;
};

const FriendsPage = () => {
    const navigate = useNavigate();
    const { user, userInfo } = useContext(UserContext);
    
    // Tab state
    const [activeTab, setActiveTab] = useState('search'); // 'search', 'requests', 'friends'
    
    // Search state
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [recentSearches, setRecentSearches] = useState([]);
    
    // Friends state
    const [friends, setFriends] = useState([]);
    const [friendRequests, setFriendRequests] = useState([]);
    const [sentRequests, setSentRequests] = useState([]);
    const [isLoadingFriends, setIsLoadingFriends] = useState(true);
    
    // Status toast
    const [status, setStatus] = useState("");
    const [showStatus, setShowStatus] = useState(false);
    const [statusType, setStatusType] = useState("success"); // 'success' or 'error'
    
    // Mobile state
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    
    const searchInputRef = useRef(null);

    // Handle resize for mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Load recent searches from localStorage
    useEffect(() => {
        const saved = localStorage.getItem('recentSearches');
        if (saved) {
            setRecentSearches(JSON.parse(saved));
        }
    }, []);

    // Fetch friends and friend requests
    useEffect(() => {
        const fetchFriendsData = async () => {
            if (!user) return;
            setIsLoadingFriends(true);
            
            try {
                const userRef = doc(db, "UserInformation", user.uid);
                const userDoc = await getDoc(userRef);
                const userData = userDoc.data();

                if (userData) {
                    // Get friend requests received
                    if (userData.friendRequests) {
                        setFriendRequests(userData.friendRequests);
                    }
                    
                    // Get friends list with full info
                    if (userData.friendsList && userData.friendsList.length > 0) {
                        const friendsData = await Promise.all(
                            userData.friendsList.map(async (friend) => {
                                try {
                                    const friendDoc = await getDoc(doc(db, "UserInformation", friend.id));
                                    if (friendDoc.exists()) {
                                        return { id: friend.id, ...friendDoc.data() };
                                    }
                                    return { id: friend.id, name: friend.name || 'Unknown' };
                                } catch (error) {
                                    return { id: friend.id, name: friend.name || 'Unknown' };
                                }
                            })
                        );
                        setFriends(friendsData);
                    }
                    
                    // Get sent requests (need to check other users' friendRequests)
                    if (userData.sentRequests) {
                        setSentRequests(userData.sentRequests);
                    }
                }
            } catch (error) {
                console.error("Error fetching friends data:", error);
            } finally {
                setIsLoadingFriends(false);
            }
        };
        
        fetchFriendsData();
    }, [user]);

    // Fetch user suggestions from Firestore with debounced search term
    useEffect(() => {
        if (debouncedSearchTerm.length < 2) {
            setSuggestions([]);
            setIsSearching(false);
            return;
        }

        const fetchUsers = async () => {
            setIsSearching(true);
            const searchWords = debouncedSearchTerm.toLowerCase().trim().split(" ");
            const endTerm = debouncedSearchTerm.toLowerCase().trim() + "\uf8ff";
            
            try {
                const queries = searchWords.slice(0, 2).map((word) =>
                    query(
                        collection(db, "UserInformation"),
                        where("searchKeyWords", "array-contains", word),
                        limit(10)
                    )
                );

                queries.push(
                    query(
                        collection(db, "UserInformation"),
                        where("nameLower", ">=", debouncedSearchTerm.toLowerCase().trim()),
                        where("nameLower", "<=", endTerm),
                        limit(10)
                    )
                );

                const snapshots = await Promise.all(queries.map(getDocs));

                let results = snapshots.flatMap(snapshot =>
                    snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                );

                // Deduplicate and filter out current user
                const uniqueResults = Array.from(new Map(results.map(usr => [usr.id, usr])).values())
                    .filter(usr => usr.id !== user?.uid)
                    .slice(0, 15);

                setSuggestions(uniqueResults);
            } catch (error) {
                console.error("Error fetching user suggestions:", error);
            } finally {
                setIsSearching(false);
            }
        };

        fetchUsers();
    }, [debouncedSearchTerm, user?.uid]);

    const showToast = (message, type = "success") => {
        setStatus(message);
        setStatusType(type);
        setShowStatus(true);
    };

    useEffect(() => {
        if (showStatus) {
            const timer = setTimeout(() => {
                setShowStatus(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showStatus]);

    const getUserName = async (userId) => {
        try {
            const userRef = doc(db, "UserInformation", userId);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
                return userDoc.data().name;
            }
            return null;
        } catch (error) {
            console.error("Error getting user name:", error);
            return null;
        }
    };

    const sendFriendRequest = async (friendId, friendName) => {
        if (!user || !friendId) return;

        try {
            const userRef = doc(db, "UserInformation", user.uid);
            const userDoc = await getDoc(userRef);
            const userData = userDoc.data();

            const friendReference = doc(db, "UserInformation", friendId);
            const friendDoc = await getDoc(friendReference);
            const friendData = friendDoc.data();

            if (friendId === user.uid) {
                showToast("Cannot send friend request to yourself.", "error");
                return;
            }

            if (userData?.friendsList?.some(friend => friend.id === friendId)) {
                showToast("Already friends!", "error");
                return;
            }

            if (friendData?.friendRequests?.some(req => req.senderId === user.uid)) {
                showToast("Friend request already sent.", "error");
                return;
            }

            const myName = await getUserName(user.uid);
            if (!myName) {
                showToast("Error getting your profile.", "error");
                return;
            }

            const timestamp = new Date().toISOString();

            // Add request to friend's friendRequests
            await updateDoc(friendReference, {
                friendRequests: arrayUnion({
                    senderId: user.uid,
                    senderName: myName,
                    timestamp: timestamp,
                }),
            });

            // Add to my sentRequests to track it
            const sentRequest = {
                recipientId: friendId,
                recipientName: friendName,
                timestamp: timestamp,
            };
            
            await updateDoc(userRef, {
                sentRequests: arrayUnion(sentRequest),
            });

            // Update local state
            setSentRequests(prev => [...prev, sentRequest]);

            // Add to recent searches
            const newRecentSearch = { id: friendId, name: friendName };
            const updatedRecent = [newRecentSearch, ...recentSearches.filter(s => s.id !== friendId)].slice(0, 5);
            setRecentSearches(updatedRecent);
            localStorage.setItem('recentSearches', JSON.stringify(updatedRecent));

            showToast("Friend request sent!", "success");
        } catch (error) {
            console.error("Error sending friend request:", error);
            showToast("Error sending request.", "error");
        }
    };

    const acceptFriendRequest = async (request) => {
        if (!user || !request) return;

        try {
            const userRef = doc(db, "UserInformation", user.uid);
            const senderRef = doc(db, "UserInformation", request.senderId);

            // Get both users' data
            const [userDocSnap, senderDocSnap] = await Promise.all([
                getDoc(userRef),
                getDoc(senderRef)
            ]);
            const userData = userDocSnap.data();
            const senderData = senderDocSnap.data();

            // VALIDATION: Check if the request still exists in my friendRequests
            const requestStillExists = userData?.friendRequests?.some(
                req => req.senderId === request.senderId && req.timestamp === request.timestamp
            );

            if (!requestStillExists) {
                // Request was cancelled or already processed - refresh local state
                setFriendRequests(userData?.friendRequests || []);
                showToast("This request is no longer available.", "error");
                return;
            }

            // Check if we're already friends (maybe processed from another device/tab)
            const alreadyFriends = userData?.friendsList?.some(f => f.id === request.senderId);
            if (alreadyFriends) {
                setFriendRequests(prev => prev.filter(req => req.senderId !== request.senderId));
                setFriends(prev => {
                    if (prev.some(f => f.id === request.senderId)) return prev;
                    return [...prev, { id: request.senderId, name: request.senderName, ...senderData }];
                });
                showToast("You're already friends!", "success");
                return;
            }

            // Find sent request from sender to remove from their sentRequests
            const sentRequestToRemove = senderData?.sentRequests?.find(
                req => req.recipientId === user.uid
            );

            // Handle mutual requests: If I also sent a request to them, clean that up too
            const mySentRequestToThem = userData?.sentRequests?.find(
                req => req.recipientId === request.senderId
            );
            const myRequestInTheirQueue = senderData?.friendRequests?.find(
                req => req.senderId === user.uid
            );

            // Build my update - add friend, remove their request from my queue
            const myUpdate = {
                friendsList: arrayUnion({ id: request.senderId, name: request.senderName }),
                friendRequests: arrayRemove(request),
            };
            // If I also sent them a request, remove it from my sentRequests
            if (mySentRequestToThem) {
                myUpdate.sentRequests = arrayRemove(mySentRequestToThem);
            }

            // Build their update - add me as friend
            const senderUpdate = {
                friendsList: arrayUnion({ id: user.uid, name: userInfo?.name || 'User' }),
            };
            // Remove their sent request to me
            if (sentRequestToRemove) {
                senderUpdate.sentRequests = arrayRemove(sentRequestToRemove);
            }
            // If my request is in their queue (mutual), remove it
            if (myRequestInTheirQueue) {
                senderUpdate.friendRequests = arrayRemove(myRequestInTheirQueue);
            }

            await Promise.all([
                updateDoc(userRef, myUpdate),
                updateDoc(senderRef, senderUpdate)
            ]);

            // Update local state - clean up both received requests and sent requests
            setFriendRequests(prev => prev.filter(req => req.senderId !== request.senderId));
            setSentRequests(prev => prev.filter(req => req.recipientId !== request.senderId));
            setFriends(prev => [...prev, { id: request.senderId, name: request.senderName, ...senderData }]);

            showToast(`You are now friends with ${request.senderName}!`, "success");
        } catch (error) {
            console.error("Error accepting friend request:", error);
            showToast("Error accepting request.", "error");
        }
    };

    const declineFriendRequest = async (request) => {
        if (!user || !request) return;

        try {
            const userRef = doc(db, "UserInformation", user.uid);
            const senderRef = doc(db, "UserInformation", request.senderId);

            // Get both users' data to handle mutual requests
            const [userDocSnap, senderDocSnap] = await Promise.all([
                getDoc(userRef),
                getDoc(senderRef)
            ]);
            const userData = userDocSnap.data();
            const senderData = senderDocSnap.data();

            // VALIDATION: Check if the request still exists
            const requestStillExists = userData?.friendRequests?.some(
                req => req.senderId === request.senderId && req.timestamp === request.timestamp
            );

            if (!requestStillExists) {
                // Request was cancelled or already processed - refresh local state
                setFriendRequests(userData?.friendRequests || []);
                showToast("This request is no longer available.", "error");
                return;
            }

            // Build my update - remove their request
            const myUpdate = {
                friendRequests: arrayRemove(request),
            };

            // If I also sent them a request (mutual), clean that up too
            const mySentRequestToThem = userData?.sentRequests?.find(
                req => req.recipientId === request.senderId
            );
            if (mySentRequestToThem) {
                myUpdate.sentRequests = arrayRemove(mySentRequestToThem);
            }

            await updateDoc(userRef, myUpdate);

            // Also remove their sentRequest tracking and my request from their queue if mutual
            const theirSentRequestToMe = senderData?.sentRequests?.find(
                req => req.recipientId === user.uid
            );
            const myRequestInTheirQueue = senderData?.friendRequests?.find(
                req => req.senderId === user.uid
            );

            if (theirSentRequestToMe || myRequestInTheirQueue) {
                const senderUpdate = {};
                if (theirSentRequestToMe) {
                    senderUpdate.sentRequests = arrayRemove(theirSentRequestToMe);
                }
                if (myRequestInTheirQueue) {
                    senderUpdate.friendRequests = arrayRemove(myRequestInTheirQueue);
                }
                await updateDoc(senderRef, senderUpdate);
            }

            // Update local state
            setFriendRequests(prev => prev.filter(req => req.senderId !== request.senderId));
            setSentRequests(prev => prev.filter(req => req.recipientId !== request.senderId));
            showToast("Friend request declined.", "success");
        } catch (error) {
            console.error("Error declining friend request:", error);
            showToast("Error declining request.", "error");
        }
    };

    const cancelSentRequest = async (sentRequest) => {
        if (!user || !sentRequest) return;

        try {
            const userRef = doc(db, "UserInformation", user.uid);
            const recipientRef = doc(db, "UserInformation", sentRequest.recipientId);

            // Remove from my sentRequests
            await updateDoc(userRef, {
                sentRequests: arrayRemove(sentRequest),
            });

            // Remove from recipient's friendRequests
            const recipientDoc = await getDoc(recipientRef);
            const recipientData = recipientDoc.data();
            const requestToRemove = recipientData?.friendRequests?.find(
                req => req.senderId === user.uid
            );

            if (requestToRemove) {
                await updateDoc(recipientRef, {
                    friendRequests: arrayRemove(requestToRemove),
                });
            }

            setSentRequests(prev => prev.filter(req => req.recipientId !== sentRequest.recipientId));
            showToast("Friend request cancelled.", "success");
        } catch (error) {
            console.error("Error cancelling friend request:", error);
            showToast("Error cancelling request.", "error");
        }
    };

    const removeFriend = async (friendId, friendName) => {
        if (!user || !friendId) return;

        try {
            const userRef = doc(db, "UserInformation", user.uid);
            const friendRef = doc(db, "UserInformation", friendId);

            // Get user's current friendsList to find the exact object to remove
            const userDoc = await getDoc(userRef);
            const userData = userDoc.data();
            const friendToRemove = userData?.friendsList?.find(f => f.id === friendId);

            const friendDoc = await getDoc(friendRef);
            const friendData = friendDoc.data();
            const meToRemove = friendData?.friendsList?.find(f => f.id === user.uid);

            if (friendToRemove) {
                await updateDoc(userRef, {
                    friendsList: arrayRemove(friendToRemove),
                });
            }

            if (meToRemove) {
                await updateDoc(friendRef, {
                    friendsList: arrayRemove(meToRemove),
                });
            }

            setFriends(prev => prev.filter(f => f.id !== friendId));
            showToast(`Removed ${friendName} from friends.`, "success");
        } catch (error) {
            console.error("Error removing friend:", error);
            showToast("Error removing friend.", "error");
        }
    };

    const handleViewProfile = (userId) => {
        if (userId) {
            navigate(`/profile/${userId}`);
        }
    };

    const handleStartChat = (friendId) => {
        navigate(`/messages?startChat=${friendId}`);
    };

    const clearRecentSearch = (searchId) => {
        const updated = recentSearches.filter(s => s.id !== searchId);
        setRecentSearches(updated);
        localStorage.setItem('recentSearches', JSON.stringify(updated));
    };

    const clearAllRecentSearches = () => {
        setRecentSearches([]);
        localStorage.removeItem('recentSearches');
    };

    const isFriend = (userId) => {
        return friends.some(f => f.id === userId);
    };

    const renderSearchContent = () => (
        <div className="search-content">
            {/* Search Input */}
            <div className="search-input-container">
                <Search className="search-icon" />
                <input
                    ref={searchInputRef}
                    type="text"
                    className="search-input"
                    placeholder="Search for people..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus={!isMobile}
                />
                {searchTerm && (
                    <button 
                        className="clear-search-btn"
                        onClick={() => setSearchTerm("")}
                    >
                        <Close sx={{ fontSize: 20 }} />
                    </button>
                )}
            </div>

            {/* Search Results */}
            {searchTerm.length >= 2 ? (
                <div className="search-results">
                    {isSearching ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <span>Searching...</span>
                        </div>
                    ) : suggestions.length > 0 ? (
                        <div className="results-list">
                            {suggestions.map((usr) => (
                                <div key={usr.id} className="user-card">
                                    <div 
                                        className="user-info"
                                        onClick={() => handleViewProfile(usr.id)}
                                    >
                                        <Avatar
                                            src={usr.profilePictureUrl}
                                            sx={{ width: 50, height: 50, bgcolor: '#E9505F' }}
                                        >
                                            {usr.name?.[0] || '?'}
                                        </Avatar>
                                        <div className="user-details">
                                            <span className="user-name">{usr.name}</span>
                                            {usr.bio && <span className="user-bio">{usr.bio.substring(0, 50)}{usr.bio.length > 50 ? '...' : ''}</span>}
                                        </div>
                                    </div>
                                    <div className="user-actions">
                                        {isFriend(usr.id) ? (
                                            <button 
                                                className="action-btn friends-btn"
                                                onClick={() => handleStartChat(usr.id)}
                                            >
                                                <Message sx={{ fontSize: 18 }} />
                                                <span>Message</span>
                                            </button>
                                        ) : (
                                            <button 
                                                className="action-btn add-btn"
                                                onClick={() => sendFriendRequest(usr.id, usr.name)}
                                            >
                                                <PersonAdd sx={{ fontSize: 18 }} />
                                                <span>Add</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-results">
                            <Person sx={{ fontSize: 48, color: '#ddd' }} />
                            <span>No users found for "{searchTerm}"</span>
                        </div>
                    )}
                </div>
            ) : (
                /* Recent Searches */
                <div className="recent-searches">
                    <div className="section-header">
                        <h3>Recent</h3>
                        {recentSearches.length > 0 && (
                            <button 
                                className="clear-all-btn"
                                onClick={clearAllRecentSearches}
                            >
                                Clear all
                            </button>
                        )}
                    </div>
                    {recentSearches.length > 0 ? (
                        <div className="recent-list">
                            {recentSearches.map((search) => (
                                <div key={search.id} className="recent-item">
                                    <div 
                                        className="recent-info"
                                        onClick={() => handleViewProfile(search.id)}
                                    >
                                        <Avatar
                                            sx={{ width: 44, height: 44, bgcolor: '#E9505F' }}
                                        >
                                            {search.name?.[0] || '?'}
                                        </Avatar>
                                        <span className="recent-name">{search.name}</span>
                                    </div>
                                    <button 
                                        className="remove-recent-btn"
                                        onClick={() => clearRecentSearch(search.id)}
                                    >
                                        <Close sx={{ fontSize: 18 }} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <Search sx={{ fontSize: 48, color: '#ddd' }} />
                            <span>No recent searches</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    const renderRequestsContent = () => (
        <div className="requests-content">
            {/* Received Requests */}
            <div className="section-header">
                <h3>Received Requests</h3>
                {friendRequests.length > 0 && (
                    <span className="badge">{friendRequests.length}</span>
                )}
            </div>
            
            {isLoadingFriends ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <span>Loading requests...</span>
                </div>
            ) : friendRequests.length > 0 ? (
                <div className="requests-list">
                    {friendRequests.map((request, index) => (
                        <div key={index} className="request-card">
                            <div 
                                className="request-info"
                                onClick={() => handleViewProfile(request.senderId)}
                            >
                                <Avatar
                                    sx={{ width: 56, height: 56, bgcolor: '#E9505F' }}
                                >
                                    {request.senderName?.[0] || '?'}
                                </Avatar>
                                <div className="request-details">
                                    <span className="request-name">{request.senderName}</span>
                                    <span className="request-time">
                                        {new Date(request.timestamp).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>
                            <div className="request-actions">
                                <button 
                                    className="accept-btn"
                                    onClick={() => acceptFriendRequest(request)}
                                >
                                    <Check sx={{ fontSize: 20 }} />
                                    <span>Accept</span>
                                </button>
                                <button 
                                    className="decline-btn"
                                    onClick={() => declineFriendRequest(request)}
                                >
                                    <Close sx={{ fontSize: 20 }} />
                                    <span>Decline</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="empty-state-small">
                    <span>No received requests</span>
                </div>
            )}

            {/* Sent Requests */}
            <div className="section-header" style={{ marginTop: '24px' }}>
                <h3>Sent Requests</h3>
                {sentRequests.length > 0 && (
                    <span className="badge sent">{sentRequests.length}</span>
                )}
            </div>

            {isLoadingFriends ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <span>Loading...</span>
                </div>
            ) : sentRequests.length > 0 ? (
                <div className="requests-list">
                    {sentRequests.map((request, index) => (
                        <div key={index} className="request-card sent">
                            <div 
                                className="request-info"
                                onClick={() => handleViewProfile(request.recipientId)}
                            >
                                <Avatar
                                    sx={{ width: 56, height: 56, bgcolor: '#9D79C6' }}
                                >
                                    {request.recipientName?.[0] || '?'}
                                </Avatar>
                                <div className="request-details">
                                    <span className="request-name">{request.recipientName}</span>
                                    <span className="request-time">
                                        <Send sx={{ fontSize: 12, marginRight: '4px' }} />
                                        Sent {new Date(request.timestamp).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>
                            <div className="request-actions">
                                <button 
                                    className="cancel-btn"
                                    onClick={() => cancelSentRequest(request)}
                                >
                                    <Close sx={{ fontSize: 20 }} />
                                    <span>Cancel</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="empty-state-small">
                    <span>No sent requests</span>
                </div>
            )}
        </div>
    );

    const renderFriendsContent = () => (
        <div className="friends-content">
            <div className="section-header">
                <h3>Friends</h3>
                {friends.length > 0 && (
                    <span className="badge">{friends.length}</span>
                )}
            </div>
            
            {isLoadingFriends ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <span>Loading friends...</span>
                </div>
            ) : friends.length > 0 ? (
                <div className="friends-list">
                    {friends.map((friend) => (
                        <div key={friend.id} className="friend-card">
                            <div 
                                className="friend-info"
                                onClick={() => handleViewProfile(friend.id)}
                            >
                                <Avatar
                                    src={friend.profilePictureUrl}
                                    sx={{ width: 56, height: 56, bgcolor: '#E9505F' }}
                                >
                                    {friend.name?.[0] || '?'}
                                </Avatar>
                                <div className="friend-details">
                                    <span className="friend-name">{friend.name}</span>
                                    {friend.bio && (
                                        <span className="friend-bio">
                                            {friend.bio.substring(0, 40)}{friend.bio.length > 40 ? '...' : ''}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="friend-actions">
                                <button 
                                    className="message-btn"
                                    onClick={() => handleStartChat(friend.id)}
                                >
                                    <Message sx={{ fontSize: 20 }} />
                                </button>
                                <button 
                                    className="remove-btn"
                                    onClick={() => removeFriend(friend.id, friend.name)}
                                    title="Remove friend"
                                >
                                    <Close sx={{ fontSize: 20 }} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="empty-state">
                    <Person sx={{ fontSize: 48, color: '#ddd' }} />
                    <span>No friends yet</span>
                    <p>Search for people to add as friends!</p>
                </div>
            )}
        </div>
    );

    return (
        <Layout pageTitle="Friends">
            <div className="friends-page">
                {/* Tabs */}
                <div className="friends-tabs">
                    <button 
                        className={`tab-btn ${activeTab === 'search' ? 'active' : ''}`}
                        onClick={() => setActiveTab('search')}
                    >
                        <Search sx={{ fontSize: 20 }} />
                        <span>Search</span>
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'requests' ? 'active' : ''}`}
                        onClick={() => setActiveTab('requests')}
                    >
                        <PersonAdd sx={{ fontSize: 20 }} />
                        <span>Requests</span>
                        {friendRequests.length > 0 && (
                            <span className="tab-badge">{friendRequests.length}</span>
                        )}
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'friends' ? 'active' : ''}`}
                        onClick={() => setActiveTab('friends')}
                    >
                        <Person sx={{ fontSize: 20 }} />
                        <span>Friends</span>
                    </button>
                </div>

                {/* Tab Content */}
                <div className="tab-content">
                    {activeTab === 'search' && renderSearchContent()}
                    {activeTab === 'requests' && renderRequestsContent()}
                    {activeTab === 'friends' && renderFriendsContent()}
                </div>

                {/* Status Toast */}
                {showStatus && (
                    <div className={`status-toast ${statusType}`}>
                        {status}
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default FriendsPage;
